<template>
  <div class="relative space-y-2">
    <h3 v-if="showTitle">
      {{ t('extras') }}
      <slot name="title-suffix" />
    </h3>
    <div v-for="(extra, i) in visibleExtrasData" :key="i">
      <div v-if="!immutable || extra.isChosenOption" class="grid w-full md:grid-cols-2">
        <div v-if="!props.immutable" class="flex items-center gap-2">
          <Select
            v-model="bookedExtrasMap[extra.id]"
            :placeholder="t(`vehicleExtras.${extra.type}`)"
            :options="extra.options"
            class="w-full"
            :data-testid="`extra-select-${extra.type}`"
            :disabled="isExtraDisabled(extra.type)"
            @update:model-value="onExtraChange(extra)"
          />
          <Information v-if="extra.type === VehicleExtraType.CROSSBORDER"
            ><template #modal>{{ crossborderCountriesLabel }}</template></Information
          >
          <Information v-if="extra.type === VehicleExtraType.EXTRA_KM"
            ><template #modal>{{
              t('extrasKmInfo', {
                price: formatCurrency(priceCalculation.pricePerExtraKm),
              })
            }}</template></Information
          >
        </div>
        <div :class="{ 'px-3 py-2 text-xs': !props.immutable }">
          <div
            v-if="
              !props.immutable &&
              extra.type === VehicleExtraType.EXTRA_KM &&
              isCustomKmOptionSelected(extra.id) &&
              Number(maxExtraKmOption?.value)
            "
            class="flex items-center gap-2"
          >
            <Input
              v-model="customKmInput"
              type="number"
              step="1"
              :placeholder="`>${Number(maxExtraKmOption?.value)} km`"
              :disabled="isExtraDisabled(extra.type)"
              :is-error="!isValid"
              adornment="km"
              @input="handleCustomKmInput"
              @blur="handleCustomKmInput.flush()"
            />
            <span v-if="showCustomKmMinError" class="text-red-500">
              {{ t('customKmMinError', { min: Number(maxExtraKmOption?.value) + 1 }) }}
            </span>
            <span v-else-if="customKmValue > 0" class="font-medium text-success">
              {{ formatCurrency(displayedCustomKmPrice) }} ✓
            </span>
          </div>
          <div
            v-else
            class="flex items-center gap-2 self-center font-medium text-red-500"
            :class="{
              'text-success': extra.isChosenOption,
            }"
          >
            {{ extra.chosenOptionInfo }}
            {{ extra.isChosenOption ? '✓' : '✕' }}
            <Information v-if="extra.type === VehicleExtraType.CROSSBORDER && immutable"
              ><template #modal>{{ crossborderCountriesLabel }}</template></Information
            >
          </div>
        </div>
      </div>
    </div>

    <div v-if="!immutable && !disabled && hiddenExtrasData.length > 0" class="flex">
      <Dropdown
        position="left"
        :buttons="
          hiddenExtrasData.map((extra) => ({
            title: t(`vehicleExtras.${extra.type}`),
            onClick: () => showExtra(extra.type),
          }))
        "
      >
        <template #trigger>
          <CVButton variant="ghost" @click.prevent="">
            <span class="mr-1">+</span> {{ t('addExtra') }}
          </CVButton>
        </template>
      </Dropdown>
    </div>

    <div
      v-if="isLoading"
      class="absolute -inset-1 flex items-center justify-center bg-black/20"
      data-testid="extras-loading"
    >
      <Spinner />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { getPricingExtrasForPeriod, isChosenBookedExtra } from '@/utils/pricing.utils';
import Information from '@/components/Information.vue';
import Select from '@/components/Select.vue';
import Dropdown from '@/components/Dropdown.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import { useI18n } from 'vue-i18n';
import { formatCurrency } from '@/utils/format-numbers';
import { computed, ref, watch } from 'vue';
import { type UpdateCustomBookedExtraDto, VehicleExtraType } from '@carvia/ros-client-types';
import type { Pricing } from '@/entities/pricing/pricing.entity';
import type { CustomBookedExtra } from '@/entities/bookings/booked-extra';
import Input from '../Input.vue';
import { debounce, round } from 'lodash';
import { formatCountryCodes, getCrossborderInfoCountries } from '@/utils/crossborder-countries';

export type ExtraOptionWithMeta = {
  label: string;
  value: string;
  chosenOptionInfo: string;
  id: string;
  price: number;
  labelSuffix?: string;
};

export type BookedExtraSelection = {
  vehicleExtraId: string;
  vehicleExtraOptionId: string;
};

// Special option IDs that shouldn't be emitted as real selections
const UNCHANGED_OPTION_IDS = ['individual', 'unchanged', 'custom'];

const props = withDefaults(
  defineProps<{
    priceCalculation: Pricing;
    periodIndex?: number | null;
    immutable?: boolean;
    isLoading?: boolean;
    disabled?: boolean;
    disabledExtraTypes?: VehicleExtraType[];
    showTitle?: boolean;
  }>(),
  {
    showTitle: true,
    periodIndex: undefined,
  },
);

const extrasSource = computed(() =>
  getPricingExtrasForPeriod(props.priceCalculation, props.periodIndex),
);

const emit = defineEmits<{
  (e: 'update:bookedExtras', value: BookedExtraSelection[]): void;
  (e: 'update:customBookedExtras', value: UpdateCustomBookedExtraDto[] | undefined): void;
  (e: 'update:isValid', value: boolean): void;
  (e: 'change', value: Record<string, string>): void;
}>();

const { t, locale } = useI18n();

const crossborderCountriesLabel = computed(() => {
  const countries = getCrossborderInfoCountries(props.priceCalculation);
  return t('crossBorderInfoDynamic', {
    countries: formatCountryCodes(countries, props.priceCalculation, locale.value),
  });
});

function getInitialBookedExtrasMap(): Record<string, string> {
  const res: Record<string, string> = {};
  const { catalog, bookedExtras, customBookedExtras } = extrasSource.value;
  for (const extra of catalog) {
    const hasCustomExtra = customBookedExtras.some(
      (ce) => ce.customVehicleExtraType === extra.type && !ce.isAdditional,
    );

    const bookedExtra = bookedExtras.find((be) => be.vehicleExtraSnapshot.type === extra.type);

    if (hasCustomExtra) {
      // Use 'individual' for EXTRA_KM custom extras, 'custom' for others
      if (extra.type === VehicleExtraType.EXTRA_KM) {
        res[extra.id] = 'individual';
      } else {
        res[extra.id] = 'custom';
      }
    } else if (bookedExtra) {
      res[extra.id] = 'unchanged';
    } else {
      res[extra.id] = extra.options[0].id;
    }
  }
  return res;
}

const bookedExtrasMap = ref<Record<string, string>>(getInitialBookedExtrasMap());

const existingCustomKm = ref<CustomBookedExtra | null>(
  extrasSource.value.customBookedExtras.find(
    (extra) => extra.customVehicleExtraType === VehicleExtraType.EXTRA_KM && !extra.isAdditional,
  ) ?? null,
);

const customKmInput = ref<number | null>(existingCustomKm.value?.value ?? null);
const customKmValue = ref(existingCustomKm.value?.value ?? 0);

const maxExtraKmOption = computed(() =>
  extrasSource.value.catalog
    .find((extra) => extra.type === VehicleExtraType.EXTRA_KM)
    ?.options?.at(-1),
);

const extraKmPricePerKm = computed(() => {
  if (!maxExtraKmOption.value || Number(maxExtraKmOption.value.value) === 0) return 0;
  return (maxExtraKmOption.value.calculatedPrice ?? 0) / Number(maxExtraKmOption.value.value);
});

const customKmPrice = computed(() => {
  if (customKmValue.value <= 0) return 0;
  return round(extraKmPricePerKm.value * customKmValue.value, 2);
});

const displayedCustomKmPrice = computed(() => {
  if (customKmValue.value <= 0) return 0;
  const isValueUnchanged = customKmValue.value === existingCustomKm.value?.value;
  if (isValueUnchanged && existingCustomKm.value != null) {
    return existingCustomKm.value.price ?? 0;
  }
  return customKmPrice.value;
});

const isExtraDisabled = (extraType: VehicleExtraType) => {
  return (props.disabled || props.disabledExtraTypes?.includes(extraType)) ?? false;
};

const isCustomKmOptionSelected = (extraId: string) => {
  return bookedExtrasMap.value[extraId] === 'individual';
};

const isCustomKmValueUnchanged = computed(
  () => existingCustomKm.value != null && customKmInput.value === existingCustomKm.value.value,
);

const showCustomKmMinError = computed(() => {
  if (!customKmInput.value) return false;
  const maxKm = Number(maxExtraKmOption?.value?.value ?? 0);
  if (customKmInput.value > maxKm) return false;
  if (customKmInput.value === existingCustomKm.value?.value) return false;
  return true;
});

const isValid = computed(() => {
  const minCustomKm = Number(maxExtraKmOption?.value?.value ?? 0) + 1;
  const kmExtra = extrasSource.value.catalog.find(
    (extra) => extra.type === VehicleExtraType.EXTRA_KM,
  );
  const isIndividualOptionSelected = isCustomKmOptionSelected(kmExtra?.id ?? '');
  if (!isIndividualOptionSelected) return true;
  if (isCustomKmValueUnchanged.value) return true;
  return (customKmInput.value ?? 0) >= minCustomKm;
});

// Emit validity changes
watch(
  isValid,
  (newValue) => {
    emit('update:isValid', newValue);
  },
  { immediate: true },
);

const emitBookedExtras = () => {
  const bookedExtras: BookedExtraSelection[] = [];
  for (const extra of extrasSource.value.catalog) {
    const selectedValue = bookedExtrasMap.value[extra.id];
    if (selectedValue && !UNCHANGED_OPTION_IDS.includes(selectedValue)) {
      bookedExtras.push({
        vehicleExtraId: extra.id,
        vehicleExtraOptionId: selectedValue,
      });
    }
  }
  emit('update:bookedExtras', bookedExtras);
};

const emitCustomBookedExtras = () => {
  const kmExtra = extrasSource.value.catalog.find((e) => e.type === VehicleExtraType.EXTRA_KM);
  const minCustomKm = Number(maxExtraKmOption.value?.value ?? 0) + 1;
  if (!kmExtra || !isCustomKmOptionSelected(kmExtra.id) || customKmValue.value < minCustomKm) {
    emit('update:customBookedExtras', undefined);
    return;
  }
  if (customKmValue.value === existingCustomKm.value?.value) {
    emit('update:customBookedExtras', undefined);
    return;
  }
  const customExtras = extrasSource.value.customBookedExtras.filter(
    (e) => e.customVehicleExtraType !== VehicleExtraType.EXTRA_KM || e.isAdditional,
  );
  customExtras.push({
    description: t('customExtraKmDescription', { km: customKmValue.value }),
    price: customKmPrice.value,
    customVehicleExtraType: VehicleExtraType.EXTRA_KM,
    value: customKmValue.value,
  });
  emit('update:customBookedExtras', customExtras);
};

const handleCustomKmInput = debounce(() => {
  const inputValue = customKmInput.value;
  customKmValue.value = inputValue ?? 0;
  emitCustomBookedExtras();
}, 1000);

const clearCustomKm = () => {
  customKmInput.value = null;
  customKmValue.value = 0;
  emitCustomBookedExtras();
};

// When selecting a standard option for EXTRA_KM, clear custom km
// When selecting "individual" option, keep custom km (or wait for input)
const onExtraChange = (extra: (typeof extrasSelectData.value)[number]) => {
  if (extra.type === VehicleExtraType.EXTRA_KM) {
    const selectedValue = bookedExtrasMap.value[extra.id];
    if (selectedValue !== 'individual') {
      // Selecting a standard option, clear custom km
      clearCustomKm();
    }
  }
  emitBookedExtras();
};

const availableExtras = computed(() =>
  [...extrasSource.value.catalog].sort((extraA, extraB) => (extraA.type > extraB.type ? 1 : -1)),
);

const extraOptionName = (
  option: { name?: string | null; value?: string | null } | null | undefined,
): string => {
  if (!option) {
    return '';
  }
  if (option.value === 'true') {
    return '';
  }
  if (option.value === 'false') {
    return t('noOption');
  }
  return option.name?.length ? option.name : (option.value ?? '');
};

const extrasSelectData = computed(() =>
  availableExtras.value.map((extra) => {
    const bookedExtra = extrasSource.value.bookedExtras.find(
      (bookedExtra) => bookedExtra.vehicleExtraSnapshot.type === extra.type,
    );
    const customExtra = extrasSource.value.customBookedExtras.find(
      (customExtra) =>
        customExtra.customVehicleExtraType === extra.type && !customExtra.isAdditional,
    );

    const options: ExtraOptionWithMeta[] = [];

    if (customExtra && extra.type !== VehicleExtraType.EXTRA_KM) {
      options.push({
        label: t('customExtra'),
        value: 'custom',
        chosenOptionInfo: `${t('customExtra')} | ${formatCurrency(customExtra.price)}`,
        id: 'custom',
        price: customExtra.price,
      });
    }

    if (bookedExtra) {
      const option = bookedExtra.vehicleExtraOptionSnapshot;
      const price = bookedExtra.calculatedPrice ?? 0;
      const notPerDay = extra.type === VehicleExtraType.EXTRA_KM;
      const priceLabel = notPerDay
        ? formatCurrency(price)
        : `${formatCurrency(price / extrasSource.value.days)}/${t('day')}`;

      const name = extraOptionName(option);

      const labelSuffix =
        bookedExtra.vehicleExtraSnapshot.id !== extra.id
          ? t('fromFormerVehicle')
          : t('currentSelection');

      options.push({
        label: `${name} ${t(`vehicleExtras.${extra.type}`)} - ${priceLabel} | (${labelSuffix})`,
        value: 'unchanged',
        chosenOptionInfo: `${name} ${t(`vehicleExtras.${extra.type}`)} | ${formatCurrency(price)}`,
        id: 'unchanged',
        price,
        labelSuffix,
      });
    }

    const notPerDay = extra.type === VehicleExtraType.EXTRA_KM;
    (extra.options ?? []).forEach((option) => {
      const price = option.calculatedPrice ?? 0;

      const priceLabel = notPerDay
        ? `${formatCurrency(price)}`
        : `${formatCurrency(price / extrasSource.value.days)}/${t('day')}`;

      const name = extraOptionName(option);
      const label = `${name} ${t(`vehicleExtras.${extra.type}`)} - ${priceLabel}`;
      const chosenOptionInfo = `${name} ${t(
        `vehicleExtras.${extra.type}`,
      )} | ${formatCurrency(price)}`;
      options.push({
        label,
        value: option.id,
        chosenOptionInfo,
        id: option.id,
        price,
      });
    });

    if (extra.type === VehicleExtraType.EXTRA_KM) {
      options.push({
        label: t('individualOption'),
        value: 'individual',
        chosenOptionInfo: t('individualOption'),
        id: 'individual',
        price: 0,
      });
    }

    const selectedOption = options.find((option) => option.id === bookedExtrasMap.value[extra.id]);
    const { chosenOptionInfo } = selectedOption ?? options[0];

    return {
      id: extra.id,
      type: extra.type,
      options,
      chosenOptionInfo,
      isChosenOption: !!customExtra || isChosenBookedExtra(bookedExtra),
    };
  }),
);

const HIDDEN_BY_DEFAULT_EXTRA_TYPES = [
  VehicleExtraType.BABY_SEAT,
  VehicleExtraType.CHILD_SEAT_0,
  VehicleExtraType.CHILD_SEAT_1,
  VehicleExtraType.BOOSTER_SEAT,
  VehicleExtraType.YOUNG_DRIVER,
  VehicleExtraType.TOW_HITCH,
];

function getInitialVisibleExtraTypes(): Set<VehicleExtraType> {
  const visible = new Set<VehicleExtraType>();
  const { catalog, bookedExtras, customBookedExtras } = extrasSource.value;
  for (const extra of catalog) {
    if (!HIDDEN_BY_DEFAULT_EXTRA_TYPES.includes(extra.type)) {
      visible.add(extra.type);
    }
  }
  for (const customExtra of customBookedExtras) {
    if (customExtra.customVehicleExtraType) {
      visible.add(customExtra.customVehicleExtraType as VehicleExtraType);
    }
  }

  for (const bookedExtra of bookedExtras) {
    const extraType = bookedExtra.vehicleExtraSnapshot.type;
    const hasValue = (bookedExtra.calculatedPrice ?? 0) > 0;

    if (hasValue || !HIDDEN_BY_DEFAULT_EXTRA_TYPES.includes(extraType)) {
      visible.add(extraType);
    }
  }

  return visible;
}

const visibleExtraTypes = ref<Set<VehicleExtraType>>(getInitialVisibleExtraTypes());

// Show an extra that was hidden
const showExtra = (extraType: VehicleExtraType) => {
  visibleExtraTypes.value = new Set([...visibleExtraTypes.value, extraType]);
};

// Filter visible extras based on internal visibleExtraTypes
const visibleExtrasData = computed(() => {
  return extrasSelectData.value.filter((extra) => visibleExtraTypes.value.has(extra.type));
});

// Hidden extras for "Add Extra" dropdown
const hiddenExtrasData = computed(() => {
  return extrasSelectData.value.filter(
    (extra) => !visibleExtraTypes.value.has(extra.type) && !isExtraDisabled(extra.type),
  );
});
</script>

<i18n lang="json">
{
  "en": {
    "extras": "Extras",
    "extrasKmInfo": "Each additional km will be charged with {price}",
    "noOption": "No",
    "day": "day",
    "individualOption": "Individual Extra KM",
    "customKmMinError": "Min. {min} km",
    "customExtraKmDescription": "Individual extra km package ({km} km)",
    "customExtra": "Custom Extra",
    "fromFormerVehicle": "From former Vehicle",
    "currentSelection": "Current Selection",
    "addExtra": "Add Extra"
  },
  "de": {
    "extras": "Extras",
    "extrasKmInfo": "Jeder zusätzliche Km wird mit {price} berechnet",
    "noOption": "Kein",
    "day": "Tag",
    "individualOption": "Individuelle Zusatz-KM",
    "customKmMinError": "Min. {min} km",
    "customExtraKmDescription": "Individuelles Extrakilometerpaket ({km} km)",
    "customExtra": "Individuelles Extra",
    "fromFormerVehicle": "Von vorherigem Fahrzeug",
    "currentSelection": "Aktuelle Auswahl",
    "addExtra": "Extra hinzufügen"
  },
  "es": {
    "extras": "Extras",
    "extrasKmInfo": "Cada km adicional se facturará a {price}",
    "noOption": "Ninguno",
    "day": "día",
    "individualOption": "Km adicionales a medida",
    "customKmMinError": "Mín. {min} km",
    "customExtraKmDescription": "Paquete de km adicionales a medida ({km} km)",
    "customExtra": "Extra a medida",
    "fromFormerVehicle": "Del vehículo anterior",
    "currentSelection": "Selección actual",
    "addExtra": "Añadir extra"
  }
}
</i18n>
